import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Briefcase, CheckCircle2, RefreshCw, Plus } from 'lucide-react';
import type { CargoContract } from '@sstexames/shared-types';
import { cargosService } from '../../services/cargos.service';
import { setoresService } from '../../services/setores.service';
import { useToast } from '../Toast';

interface ModalCriarCargoSetorProps {
  isOpen: boolean;
  onClose: () => void;
  setor: { setor_id: number; setor_nome: string } | null;
  cargosList?: Array<{ id: number; nome: string }>;
  setoresList?: Array<{ id: number; nome: string; is_prestacao_servico?: boolean }>;
  empresaNome?: string;
  empresaId?: number;
  onCreated: (novoCargo: CargoContract) => void;
}

export function ModalCriarCargoSetor({
  isOpen,
  onClose,
  setor,
  setoresList,
  empresaNome,
  empresaId,
  onCreated,
}: ModalCriarCargoSetorProps) {
  const toast = useToast();

  const [temSemelhante, setTemSemelhante] = useState(true);
  const [setoresInternos, setSetoresInternos] = useState<Array<{ id: number; nome: string; is_prestacao_servico?: boolean }>>([]);
  const hasSetoresParaReferencia = Boolean(setoresInternos && setoresInternos.length > 0);
  const [setorSemelhanteId, setSetorSemelhanteId] = useState<number | ''>('');
  const [cargoSemelhanteId, setCargoSemelhanteId] = useState<number | ''>('');
  const [cargosDoSetorSemelhante, setCargosDoSetorSemelhante] = useState<Array<{ id: number; nome: string }>>([]);
  const [isLoadingCargosSetor, setIsLoadingCargosSetor] = useState(false);
  const [nome, setNome] = useState('');
  const [cbo, setCbo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [sugestoesCbo, setSugestoesCbo] = useState<Array<{ codigo: string; titulo: string }>>([]);

  const [isGeneratingIa, setIsGeneratingIa] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sucessoCriacao, setSucessoCriacao] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTemSemelhante(true);
      setSetorSemelhanteId('');
      setCargoSemelhanteId('');
      setCargosDoSetorSemelhante([]);
      setNome('');
      setCbo('');
      setDescricao('');
      setSugestoesCbo([]);
      setSucessoCriacao(false);

      if (setoresList && setoresList.length > 0) {
        setSetoresInternos(setoresList);
      } else {
        setoresService.findAll(1000).then((res) => {
          setSetoresInternos(res.data || []);
        }).catch(() => {});
      }
    }
  }, [isOpen, setoresList]);

  // Carrega os cargos pertencentes ao setor selecionado como base
  useEffect(() => {
    if (!setorSemelhanteId) {
      setCargosDoSetorSemelhante([]);
      setCargoSemelhanteId('');
      return;
    }
    setIsLoadingCargosSetor(true);
    cargosService
      .findAll({ setor_id: Number(setorSemelhanteId), empresa_id: empresaId, limit: 100 })
      .then((res) => {
        setCargosDoSetorSemelhante(res.data || []);
      })
      .catch((err) => {
        console.error('Erro ao buscar cargos do setor:', err);
        setCargosDoSetorSemelhante([]);
      })
      .finally(() => {
        setIsLoadingCargosSetor(false);
      });
  }, [setorSemelhanteId, empresaId]);

  if (!isOpen || !setor) return null;

  const handlePreencherComIa = async () => {
    if (!nome.trim()) {
      toast.error('Informe o nome da função para a IA analisar.');
      return;
    }

    setIsGeneratingIa(true);
    try {
      const [cboRes, ativRes] = await Promise.allSettled([
        cargosService.sugerirCboIa(nome.trim(), descricao.trim()),
        cargosService.sugerirAtividadesIa({
          cargo_nome: nome.trim(),
          setor_nome: setor.setor_nome,
          empresa_nome: empresaNome,
        }),
      ]);

      let cboPreenchido = false;
      if (cboRes.status === 'fulfilled' && cboRes.value?.sugestoes?.length) {
        setSugestoesCbo(cboRes.value.sugestoes);
        if (!cbo) {
          setCbo(cboRes.value.sugestoes[0].codigo);
          cboPreenchido = true;
        }
      }

      let ativPreenchida = false;
      if (ativRes.status === 'fulfilled' && ativRes.value?.descricao_atividades) {
        setDescricao(ativRes.value.descricao_atividades);
        ativPreenchida = true;
      }

      if (cboPreenchido || ativPreenchida) {
        toast.success('Informações sugeridas com sucesso.');
      } else {
        toast.info('Revise e ajuste os campos sugeridos conforme necessário.');
      }
    } catch (err: unknown) {
      console.warn('Erro ao consultar IA para sugestão de cargo:', err);
      toast.error('Não foi possível consultar a IA no momento. Preencha os campos manualmente.');
    } finally {
      setIsGeneratingIa(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nome.trim()) {
      toast.error('Informe o nome do cargo.');
      return;
    }

    if (temSemelhante && !setorSemelhanteId) {
      toast.error('Selecione o setor de referência para herdar os riscos e aptidões.');
      return;
    }

    if (temSemelhante && !cargoSemelhanteId) {
      toast.error('Selecione um cargo semelhante para herdar os riscos e aptidões.');
      return;
    }

    setIsSubmitting(true);
    try {
      const novoCargo = await cargosService.create({
        nome: nome.trim(),
        cbo: cbo.trim() || undefined,
        descricao: descricao.trim() || undefined,
        setor_id: setor.setor_id,
        cargo_semelhante_id: temSemelhante && cargoSemelhanteId ? Number(cargoSemelhanteId) : undefined,
        setor_semelhante_id: temSemelhante && setorSemelhanteId ? Number(setorSemelhanteId) : undefined,
        empresa_id: empresaId,
      });

      toast.success(
        temSemelhante
          ? 'Cargo cadastrado com sucesso! Agendamentos liberados imediatamente.'
          : 'Cargo cadastrado com sucesso! Enviado para homologação técnica da equipe de SST.',
      );
      setSucessoCriacao(true);
      onCreated(novoCargo);
    } catch (err: unknown) {
      console.error('Erro ao criar cargo no setor:', err);
      const errObj = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const rawMsg = errObj?.response?.data?.message || errObj?.message || 'Falha ao criar cargo.';
      const msg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao cadastrar novo cargo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setSetorSemelhanteId('');
    setCargoSemelhanteId('');
    setCargosDoSetorSemelhante([]);
    setNome('');
    setCbo('');
    setDescricao('');
    setSugestoesCbo([]);
    setSucessoCriacao(false);
    onClose();
  };

  const modalElement = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-primary-50 text-primary-700 rounded-xl border border-primary-100">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Novo Cargo no Setor</h3>
              <p className="text-xs text-gray-500 font-medium">
                Setor: <strong className="text-primary-700">{setor.setor_nome}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleResetAndClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        {sucessoCriacao ? (
          <div className="p-8 text-center space-y-4 my-auto">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div className="space-y-2">
              <h4 className="text-lg font-bold text-gray-900">
                Cargo cadastrado com sucesso
              </h4>
              {temSemelhante ? (
                <div className="text-sm text-gray-600 max-w-sm mx-auto space-y-2">
                  <p>
                    A nova função <strong>"{nome}"</strong> foi vinculada ao setor{' '}
                    <strong>"{setor.setor_nome}"</strong> com base nos riscos e exames do cargo de referência.
                  </p>
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-left text-xs text-emerald-950 space-y-1">
                    <strong className="text-emerald-900 font-bold flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-600 shrink-0" />
                      <span>Agendamento Liberado Imediatamente:</span>
                    </strong>
                    <p className="text-[11px] text-emerald-800">
                      Você já pode agendar exames para esta função agora mesmo. A atualização formal dos laudos (PGR e PCMSO) será concluída pela equipe técnica em até <strong>5 dias úteis</strong>.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600 max-w-sm mx-auto space-y-2">
                  <p>
                    A nova função <strong>"{nome}"</strong> foi cadastrada e enviada para a equipe de Engenharia e Medicina de SST.
                  </p>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-left text-xs text-amber-950 space-y-1">
                    <strong className="text-amber-900 font-bold flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-amber-600 shrink-0" />
                      <span>Agendamento Pendente de Homologação Técnica:</span>
                    </strong>
                    <p className="text-[11px] text-amber-800">
                      Por se tratar de função inédita sem exames definidos, o agendamento ficará bloqueado até que os técnicos parametrizem os riscos e exames da NR-07 (prazo de até <strong>7 dias úteis</strong>).
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="pt-4">
              <button
                type="button"
                onClick={handleResetAndClose}
                className="w-full sm:w-auto px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl shadow-md transition-all cursor-pointer"
              >
                Concluir e Voltar ao Organograma
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
            {/* Seletor de Modalidade: Com Semelhante vs Sem Semelhante */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Como deseja parametrizar este novo cargo?
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!hasSetoresParaReferencia}
                  onClick={() => setTemSemelhante(true)}
                  className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition cursor-pointer ${
                    temSemelhante
                      ? 'bg-emerald-50/70 border-emerald-500 ring-2 ring-emerald-500/20 text-emerald-950 shadow-2xs'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  } ${!hasSetoresParaReferencia ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!hasSetoresParaReferencia ? 'Não há outros setores cadastrados nesta empresa ainda' : undefined}
                >
                  <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${temSemelhante ? 'bg-emerald-600' : 'bg-gray-300'}`} />
                  <div>
                    <div className="text-xs font-bold flex items-center gap-1.5">
                      <span>Com Cargo Semelhante</span>
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">Pré-aprovado</span>
                    </div>
                    <div className="text-[11px] text-emerald-700 font-medium mt-0.5">
                      {hasSetoresParaReferencia ? 'Agendamento liberado imediatamente' : 'Nenhum setor prévio cadastrado'}
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setTemSemelhante(false);
                    setCargoSemelhanteId('');
                  }}
                  className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition cursor-pointer ${
                    !temSemelhante
                      ? 'bg-amber-50/70 border-amber-500 ring-2 ring-amber-500/20 text-amber-950 shadow-2xs'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${!temSemelhante ? 'bg-amber-600' : 'bg-gray-300'}`} />
                  <div>
                    <div className="text-xs font-bold flex items-center gap-1.5">
                      <span>Sem Cargo Semelhante</span>
                      <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">Análise Técnica</span>
                    </div>
                    <div className="text-[11px] text-amber-700 font-medium mt-0.5">Requer homologação prévia</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Banner Dinâmico Informativo de SLA e Aprovação com distinção explícita entre Agendamento vs Laudos */}
            {temSemelhante ? (
              <div className="p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-xl space-y-1.5 text-xs text-emerald-950 shadow-2xs">
                <div className="flex items-center gap-2 font-bold text-emerald-900">
                  <span className="w-2 h-2 rounded-full bg-emerald-600 shrink-0" />
                  <span>Agendamentos Liberados Imediatamente</span>
                </div>
                <div className="text-[11px] text-emerald-900 leading-relaxed pl-3.5 space-y-1">
                  <p>
                    • <strong>Agendamento de Exames:</strong> Você já poderá agendar exames para este cargo logo após salvar, herdando toda a matriz de riscos e exames da função semelhante.
                  </p>
                  <p>
                    • <strong>Atualização Formal dos Laudos (PGR/PCMSO):</strong> O setor de engenharia de SST emitirá a nova versão dos seus documentos regulatórios com prazo de até <strong>5 dias úteis</strong>.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-xl space-y-1.5 text-xs text-amber-950 shadow-2xs">
                <div className="flex items-center gap-2 font-bold text-amber-900">
                  <span className="w-2 h-2 rounded-full bg-amber-600 shrink-0" />
                  <span>Agendamentos Pendentes de Homologação Técnica</span>
                </div>
                <div className="text-[11px] text-amber-900 leading-relaxed pl-3.5 space-y-1">
                  <p>
                    • <strong>Agendamento de Exames:</strong> Ficará temporariamente indisponível. Como se trata de uma função inédita sem exames do PCMSO definidos, não é permitido emitir guias até a homologação técnica.
                  </p>
                  <p>
                    • <strong>Prazo de Análise Técnica:</strong> Nossa equipe de Engenharia e Medicina avaliará as atribuições da função em até <strong>7 dias úteis</strong> para classificar os riscos, definir os exames obrigatórios e liberar os agendamentos.
                  </p>
                </div>
              </div>
            )}

            {/* Cargo Semelhante (apenas se temSemelhante for true) */}
            {temSemelhante && (
              <div className="space-y-3 bg-emerald-50/50 p-3.5 rounded-xl border border-emerald-200/80">
                {/* 1. Setor de Referência (Origem) */}
                <div>
                  <label className="block text-xs font-semibold text-emerald-950 mb-1">
                    1. Setor de Referência (Origem)
                    <span className="text-red-500 font-bold ml-0.5">*</span>
                  </label>
                  <select
                    required
                    value={setorSemelhanteId}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : '';
                      setSetorSemelhanteId(val);
                      setCargoSemelhanteId('');
                      setCargosDoSetorSemelhante([]);
                    }}
                    className="w-full px-3 py-2 text-sm bg-white border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-shadow outline-none text-gray-800"
                  >
                    <option value="">Selecione o setor de onde clonar...</option>
                    {setoresInternos.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome} {s.is_prestacao_servico ? '• [Prestador de Serviços]' : '• [Interno]'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 2. Cargo Semelhante naquele setor */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-emerald-950">
                      2. Cargo Semelhante (Base de Referência)
                      <span className="text-red-500 font-bold ml-0.5">*</span>
                    </label>
                    {isLoadingCargosSetor && (
                      <span className="text-[11px] text-emerald-700 flex items-center gap-1 font-medium">
                        <RefreshCw className="w-3 h-3 animate-spin" /> Carregando cargos do setor...
                      </span>
                    )}
                  </div>
                  <select
                    required
                    disabled={!setorSemelhanteId || isLoadingCargosSetor}
                    value={cargoSemelhanteId}
                    onChange={(e) => setCargoSemelhanteId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full px-3 py-2 text-sm bg-white border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-shadow outline-none text-gray-800 disabled:opacity-50 disabled:bg-gray-100"
                  >
                    <option value="">
                      {!setorSemelhanteId
                        ? 'Selecione primeiro o setor acima...'
                        : isLoadingCargosSetor
                        ? 'Carregando cargos do setor...'
                        : cargosDoSetorSemelhante.length === 0
                        ? 'Nenhum cargo encontrado neste setor'
                        : 'Selecione o cargo semelhante existente...'}
                    </option>
                    {cargosDoSetorSemelhante.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Nome da Função + Botão IA */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-gray-700">
                  Nome da Função / Cargo
                  <span className="text-red-500 font-bold ml-0.5">*</span>
                </label>

                <button
                  type="button"
                  onClick={handlePreencherComIa}
                  disabled={isGeneratingIa || !nome.trim()}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg shadow-2xs transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sugerir CBO e descrição das atividades usando inteligência técnica a partir do nome da função e setor"
                >
                  {isGeneratingIa ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-600" />
                  ) : null}
                  <span>{isGeneratingIa ? 'Analisando...' : 'Sugerir CBO e Atividades'}</span>
                </button>
              </div>

              <input
                type="text"
                required
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Operador de Empilhadeira, Almoxarife..."
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800"
              />
            </div>

            {/* CBO */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                CBO (Classificação Brasileira de Ocupações)
                <span className="text-gray-400 font-normal ml-1">(Opcional)</span>
              </label>
              <input
                type="text"
                value={cbo}
                onChange={(e) => setCbo(e.target.value)}
                placeholder="Ex: 4141-05 ou 414105"
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800 font-mono"
              />

              {/* Sugestões de CBO da IA */}
              {sugestoesCbo.length > 0 && (
                <div className="mt-2 space-y-1">
                  <span className="text-[11px] font-medium text-gray-500 block">
                    Sugestões oficiais da IA para este cargo:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {sugestoesCbo.slice(0, 3).map((sug) => (
                      <button
                        key={sug.codigo}
                        type="button"
                        onClick={() => setCbo(sug.codigo)}
                        className={`text-xs px-2 py-0.5 rounded-md border font-mono transition-colors cursor-pointer ${
                          cbo === sug.codigo
                            ? 'bg-primary-100 text-primary-800 border-primary-300 font-bold'
                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                        }`}
                        title={sug.titulo}
                      >
                        {sug.codigo} · {sug.titulo}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Descrição das Atividades */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Descrição Sumária das Atividades
                <span className="text-gray-400 font-normal ml-1">(Opcional)</span>
              </label>
              <textarea
                rows={4}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva as principais atribuições e atividades realizadas na rotina do cargo..."
                className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow outline-none text-gray-800 resize-none"
              />
            </div>

            {/* Modal Footer Buttons */}
            <div className="pt-2 flex items-center justify-end space-x-3 border-t border-gray-100">
              <button
                type="button"
                onClick={handleResetAndClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !nome.trim() || (temSemelhante && (!setorSemelhanteId || !cargoSemelhanteId))}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-xs transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-white" />
                    <span>Criando cargo...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Criar Cargo</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalElement, document.body) : modalElement;
}
